import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { CheckCircle, XCircle, Clock, Warning, Check } from "@phosphor-icons/react";

const STATUSES = [
  { key: "present", color: "bg-emerald-500 text-white", icon: CheckCircle },
  { key: "absent", color: "bg-destructive text-destructive-foreground", icon: XCircle },
  { key: "late", color: "bg-amber-500 text-white", icon: Clock },
  { key: "excused", color: "bg-sky-500 text-white", icon: Warning },
];

export default function AttendanceMarking() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [cls, setCls] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/classes").then(({ data }) => setClasses(data)); }, []);
  useEffect(() => {
    if (!classId) { setCls(null); return; }
    api.get(`/classes/${classId}`).then(({ data }) => {
      setCls(data);
      const initial = {};
      (data.students || []).forEach((s) => { initial[s.id] = "present"; });
      setMarks(initial);
    });
  }, [classId]);

  const mark = (sid, status) => setMarks((m) => ({ ...m, [sid]: status }));
  const markAll = (status) => {
    const m = {};
    (cls?.students || []).forEach((s) => { m[s.id] = status; });
    setMarks(m);
  };

  const save = async () => {
    if (!classId) return toast.error(t("attendance.toasts.selectClass"));
    setSaving(true);
    try {
      const records = Object.entries(marks).map(([student_id, status]) => ({ student_id, status }));
      await api.post("/attendance/bulk", { class_id: classId, date, records });
      const absent = records.filter((r) => r.status === "absent").length;
      toast.success(absent > 0 ? t("attendance.toasts.savedWithAbsent", { count: absent }) : t("attendance.toasts.saved"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const counts = STATUSES.reduce((acc, s) => {
    acc[s.key] = Object.values(marks).filter((v) => v === s.key).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.attendance")}</div>
        <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("attendance.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("attendance.subtitle")}</p>
      </div>

      <Card className="border-border/60"><CardContent className="p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wider font-semibold">{t("attendance.class")}</label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="attendance-class-select" className="mt-1.5 h-10"><SelectValue placeholder={t("attendance.selectClassPh")} /></SelectTrigger>
            <SelectContent>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.schedule}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full md:w-52">
          <label className="text-xs uppercase tracking-wider font-semibold">{t("attendance.date")}</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="mt-1.5 h-10 w-full px-3 rounded-md bg-background border border-input text-sm" />
        </div>
      </CardContent></Card>

      {cls && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {STATUSES.map((s) => (
              <div key={s.key} className="p-4 rounded-md border border-border/60 bg-card">
                <div className="flex items-center gap-2">
                  <s.icon size={16} weight="duotone" />
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t(`attendance.${s.key}`)}</span>
                </div>
                <div className="text-2xl font-bold font-mono mt-1">{counts[s.key] || 0}</div>
              </div>
            ))}
          </div>

          <Card className="border-border/60"><CardContent className="p-4 flex flex-wrap gap-2">
            <span className="text-xs text-muted-foreground self-center mr-2">{t("attendance.markAll")}</span>
            {STATUSES.map((s) => (
              <Button key={s.key} size="sm" variant="outline" onClick={() => markAll(s.key)}
                data-testid={`mark-all-${s.key}`} className="gap-1.5">
                <s.icon size={12} /> {t(`attendance.${s.key}`)}
              </Button>
            ))}
            <div className="ml-auto">
              <Button onClick={save} disabled={saving} data-testid="attendance-save" className="rounded-full gap-2">
                <Check size={14} weight="bold" /> {saving ? t("attendance.saving") : t("attendance.saveWithCount", { count: cls.students?.length || 0 })}
              </Button>
            </div>
          </CardContent></Card>

          <Card className="border-border/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="attendance-table">
                <thead className="bg-secondary/40 border-b border-border">
                  <tr className="text-left">
                    <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{t("attendance.student")}</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{t("attendance.id")}</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{t("attendance.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(cls.students || []).map((s) => (
                    <tr key={s.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                            {s.full_name?.charAt(0)}
                          </div>
                          <div className="font-semibold">{s.full_name}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{s.student_number}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {STATUSES.map((st) => (
                            <button
                              key={st.key}
                              data-testid={`mark-${s.id}-${st.key}`}
                              onClick={() => mark(s.id, st.key)}
                              className={cn(
                                "px-3 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1 transition-colors",
                                marks[s.id] === st.key
                                  ? st.color
                                  : "bg-secondary text-muted-foreground hover:bg-secondary/70"
                              )}
                            >
                              <st.icon size={11} /> {t(`attendance.${st.key}`)}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {!cls && (
        <Card className="border-border/60"><CardContent className="p-12 text-center text-muted-foreground">
          {t("attendance.selectAbove")}
        </CardContent></Card>
      )}
    </div>
  );
}
